import React, { useState } from 'react';
import Modal from '../../Modal';
import { useBookingApi } from './bookingApi';
import {
    BOOKING_STATUS_LABELS,
    BOOKING_STATUS_TONE,
    BOOKING_MODE_LABELS,
    formatInterval,
    formatDateTime,
    formatCents,
    bookingCustomerLabel,
} from './bookingFormat';

/**
 * View a single booking and run the seller lifecycle actions (approve, decline,
 * cancel). Semi-independent: hand it a booking (as returned by the agenda list
 * or GET /bookings/:id) and an onUpdated callback; it owns the action calls.
 */
export default function BookingDetailModal({ booking: initialBooking, onUpdated, onClose }) {
    const api = useBookingApi();
    const [booking, setBooking] = useState(initialBooking);
    const [actionRefund, setActionRefund] = useState(null);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const [reason, setReason] = useState('');

    const tz = booking.timezone || booking.resource?.timezone;
    const pricing = booking.pricing || null;
    const isPending = booking.status === 'pending';
    const isCancelable = ['pending', 'confirmed'].includes(booking.status);

    const run = async (fn) => {
        setError(null);
        setWorking(true);
        try {
            const result = await fn();
            // Cancel has {booking, refund}; decline retains booking fields and adds refund.
            const updated = result && result.booking !== undefined ? result.booking : result;
            if (onUpdated && updated) onUpdated(updated);
            if (result?.refund) {
                if (updated) setBooking(updated);
                setActionRefund(result.refund);
                setConfirmingCancel(false);
                setWorking(false);
            } else onClose();
        } catch (err) {
            setError(err.message || 'That action could not be completed.');
            setWorking(false);
        }
    };

    const statusTone = BOOKING_STATUS_TONE[booking.status] || 'muted';
    const persistedRefund = booking.metadata?.refund;
    const refund = actionRefund || (persistedRefund ? {
        refundCents: persistedRefund.amountCents, currency: persistedRefund.currency,
        execution: { status: persistedRefund.status, method: persistedRefund.method },
    } : null);
    const refundStatus = refund?.execution?.status;
    const refundExplanation = refundStatus === 'succeeded'
        ? (refund.execution.method === 'void' ? 'Payment voided.' : 'Refund submitted to the original payment method.')
        : refundStatus === 'not_required' ? 'No refund is required.'
        : refundStatus === 'awaiting_settlement' ? 'Refund awaiting payment settlement.'
        : refundStatus === 'manual_required' && refund.execution.method === 'manual_seller'
            ? 'Refund the customer through their original payment method.'
        : refundStatus === 'manual_required' ? 'Mayhem support must arrange this refund.'
        : ['in_flight', 'outcome_unknown'].includes(refundStatus)
            ? 'Refund outcome unconfirmed. Contact Mayhem support before issuing another refund.'
        : refundStatus === 'failed' ? 'Refund not processed. Contact Mayhem support.'
        : 'No refund was submitted by this action. Arrange the refund through the original payment method or Mayhem support.';

    return (
        <Modal
            isOpen
            onClose={onClose}
            title="Booking details"
            size="medium"
            className="booking-modal booking-detail-modal"
        >
            {error && <div className="error booking-inline-error">{error}</div>}
            {refund && (
                <div className="booking-inline-notice" role="status">
                    <p>Refund: {formatCents(refund.refundCents, refund.currency)}</p>
                    <p>{refundExplanation}</p>
                    {actionRefund && <button type="button" className="button secondary" onClick={onClose}>Done</button>}
                </div>
            )}

            <div className="booking-detail-head">
                <span className={`booking-badge ${statusTone}`}>
                    {BOOKING_STATUS_LABELS[booking.status] || booking.status}
                </span>
                {booking.requiresApproval && booking.status === 'pending' && (
                    <span className="booking-muted">Awaiting your approval</span>
                )}
            </div>

            <dl className="booking-detail-grid">
                <div>
                    <dt>When</dt>
                    <dd>{formatInterval(booking.startAt, booking.endAt, tz)}</dd>
                </div>
                <div>
                    <dt>Calendar</dt>
                    <dd>{booking.resource?.name || `#${booking.resourceId}`}</dd>
                </div>
                {booking.product && (
                    <div>
                        <dt>Product</dt>
                        <dd>{booking.product.name}</dd>
                    </div>
                )}
                <div>
                    <dt>Type</dt>
                    <dd>{BOOKING_MODE_LABELS[booking.mode] || booking.mode}</dd>
                </div>
                <div>
                    <dt>Customer</dt>
                    <dd>
                        {bookingCustomerLabel(booking)}
                        {booking.guestEmail && booking.guestName && (
                            <small className="booking-muted"> · {booking.guestEmail}</small>
                        )}
                    </dd>
                </div>
                <div>
                    <dt>Units</dt>
                    <dd>{booking.unitsUsed}</dd>
                </div>
                {booking.orderId && (
                    <div>
                        <dt>Order</dt>
                        <dd>#{booking.orderId}</dd>
                    </div>
                )}
                {tz && (
                    <div>
                        <dt>Timezone</dt>
                        <dd>{tz}</dd>
                    </div>
                )}
                {booking.canceledAt && (
                    <div>
                        <dt>Canceled</dt>
                        <dd>
                            {formatDateTime(booking.canceledAt, tz)}
                            {booking.canceledBy && <small className="booking-muted"> by {booking.canceledBy}</small>}
                            {booking.canceledReason && <div className="booking-muted">{booking.canceledReason}</div>}
                        </dd>
                    </div>
                )}
            </dl>

            {pricing && (
                <div className="booking-pricing">
                    <h4>Pricing</h4>
                    <ul>
                        {pricing.unitPrice !== undefined && (
                            <li>
                                <span>{pricing.units || booking.unitsUsed} × {formatCents(pricing.unitPrice, pricing.currency)}</span>
                                <span>{formatCents((pricing.unitPrice || 0) * (pricing.units || booking.unitsUsed || 1), pricing.currency)}</span>
                            </li>
                        )}
                        {Array.isArray(pricing.fees) && pricing.fees.map((fee, i) => (
                            <li key={i}>
                                <span>{fee.label}{fee.refundable ? ' (refundable)' : ''}</span>
                                <span>{formatCents(fee.amount, pricing.currency)}</span>
                            </li>
                        ))}
                        {pricing.total !== undefined && (
                            <li className="booking-pricing-total">
                                <span>Total</span>
                                <span>{formatCents(pricing.total, pricing.currency)}</span>
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {(isPending || isCancelable) && (
                <div className="booking-detail-actions">
                    <p className="help">Seller cancellations and declines owe the full remaining paid amount back to the customer.</p>
                    {isPending && (
                        <>
                            <button
                                type="button"
                                className="button primary"
                                disabled={working}
                                onClick={() => run(() => api.approveBooking(booking.id))}
                            >
                                Approve
                            </button>
                            <button
                                type="button"
                                className="button secondary"
                                disabled={working}
                                onClick={() => run(() => api.declineBooking(booking.id, reason || null))}
                            >
                                Decline
                            </button>
                        </>
                    )}
                    {isCancelable && !confirmingCancel && (
                        <button
                            type="button"
                            className="button danger"
                            disabled={working}
                            onClick={() => setConfirmingCancel(true)}
                        >
                            Cancel booking
                        </button>
                    )}
                </div>
            )}

            {confirmingCancel && (
                <div className="booking-cancel-confirm">
                    <label className="booking-field">
                        <span>Reason <small className="help">(optional, shared per policy)</small></span>
                        <input
                            type="text"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g. Double-booked, customer request"
                            maxLength={255}
                        />
                    </label>
                    <p className="help">
                        Canceling releases the reserved time. The customer is owed a full refund of
                        the remaining paid amount. The result will show whether a refund was submitted.
                    </p>
                    <div className="booking-form-actions">
                        <button type="button" className="button secondary" onClick={() => setConfirmingCancel(false)} disabled={working}>
                            Keep booking
                        </button>
                        <button
                            type="button"
                            className="button danger"
                            disabled={working}
                            onClick={() => run(() => api.cancelBooking(booking.id, reason || null))}
                        >
                            {working ? 'Canceling…' : 'Confirm cancel'}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
}
